import React, { useState, useEffect } from "react";
import {
  faCircleExclamation,
  faCirclePlus,
  faFileArrowUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  createSingleSetting,
  getAllSubPagesList,
  SettingGroupdropdown,
  SettingsValidationDropdown,
} from "services/apiService";
import toast from "react-hot-toast";
import ImageUploaderModal from "common/ImageUploaderModal";
import GoogleFontsDropdown from "./GoogleFontsDropdown";
import ReusableEditor from "common/ReusableCKEditor";
import config from "config";

interface ValidationType {
  type: string;
  placeholder: string;
  pattern: RegExp;
}

interface ValidationOption {
  id: number;
  name: keyof typeof validationTypes;
}

interface GroupOption {
  id: number;
  name: string;
}

const keyOptionsMap: Record<string, { value: string; label: string }[]> = {
  header: [
    { value: "1", label: "Header_1" },
    { value: "2", label: "Header_2" },
    { value: "3", label: "Header_3" },
    { value: "4", label: "Header_4" },
    { value: "5", label: "Header_5" },
  ],
  footer: [
    { value: "1", label: "Footer_1" },
    { value: "2", label: "Footer_2" },
  ],
};

const validationTypes: Record<string, ValidationType> = {
  TEXT_INPUT: { type: "text", placeholder: "Enter text", pattern: /.*/ },
  NUMBER_INPUT: {
    type: "text",
    placeholder: "Enter number",
    pattern: /^[\d\W]*$/,
  },
  TEXT_AREA: {
    type: "textarea",
    placeholder: "Enter description",
    pattern: /.*/,
  },
  EMAIL: {
    type: "email",
    placeholder: "Enter email",
    pattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
  },
  GST_NUMBER: {
    type: "text",
    placeholder: "Enter GST number",
    pattern: /^[0-9A-Z]{15}$/,
  },
  PAN_NUMBER: {
    type: "text",
    placeholder: "Enter PAN number",
    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  },
  IMAGE_INPUT: {
    type: "file",
    placeholder: "Select an image",
    pattern: /.*/,
  },
};

const SettingSingleCreate: React.FC<{
  open: boolean;
  handleClose: () => void;
  onActionComplete: () => void;
}> = ({ open, handleClose, onActionComplete }) => {
  const [formValues, setFormValues] = useState({
    key_name: "",
    key_value: "",
    group_id: "",
    display_name: "",
    validation_id: "",
    is_active: 1,
  });

  const [subPagesLinks, setSubPagesLinks] = useState([]);
  const AppUrl = config.REACT_APP_IMAGE_URL;
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [validationDropdown, setValidationDropdown] = useState<
    ValidationOption[]
  >([]);

  const [selectedImages, setSelectedImages] = useState(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedValidation, setSelectedValidation] =
    useState<ValidationOption | null>(null);

  const [selectedKeyvalue, setSelectedKeyvalue] = useState("");

  const isHeader = formValues.key_name.toLowerCase() !== "header";
  const isFooter = formValues.key_name.toLowerCase() !== "footer";

  const lowerKeyName = formValues?.key_name?.toLowerCase();
  const dynamicOptions = keyOptionsMap[lowerKeyName] || [];
  const showDynamicSelect = dynamicOptions?.length > 0;

  useEffect(() => {
    fetchValidationDropdown();
    fetchGroups();
    fetchPrivacyPolicyData();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await SettingGroupdropdown();
      setGroups(response.data);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const fetchValidationDropdown = async () => {
    try {
      const response = await SettingsValidationDropdown();
      if (response.status_code === 200) {
        setValidationDropdown(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  const fetchPrivacyPolicyData = async () => {
    try {
      const response = await getAllSubPagesList();
      if (response.status_code === 200) {
        setSubPagesLinks(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    if (name === "validation_id") {
      setFormValues((prev) => ({
        ...prev,
        validation_id: value,
        key_name: "",
        key_value: "",
        group_id: "",
        display_name: "",
        is_active: 1,
      }));
      setSelectedValidation(
        validationDropdown.find((item) => item.id === Number(value)) || null
      );
    } else {
      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: value ? "" : "This field is required",
      }));
    }
  };
  const handleFontSelection = (fontData: {
    fontFamily: string;
    fontCDN: string;
  }) => {
    setFormValues((prev) => ({
      ...prev,
      key_value: JSON.stringify(fontData),
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    if (selectedValidation) {
      const validationRule = validationTypes[selectedValidation.name]?.pattern;
      setErrors((prev) => ({
        ...prev,
        [name]: validationRule?.test(value) ? "" : "Invalid format",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const keyValue =
        lowerKeyName === "header" || lowerKeyName === "footer"
          ? selectedKeyvalue
          : String(formValues.key_value);

      const payload = {
        key_name: formValues.key_name,
        group_id: formValues.group_id,
        display_name: formValues.display_name,
        validation_id: formValues.validation_id,
        is_active: 1,
        key_value: keyValue,
        validation_key: Number(formValues.validation_id),
        is_viewed: 1,
      };
      const response = await createSingleSetting(payload);
      if (response.status_code === 200) {
        toast.success("Setting created successfully");
        onActionComplete();
        handleClose();
        setFormValues({
          key_name: "",
          key_value: "",
          group_id: "",
          display_name: "",
          validation_id: "",
          is_active: 1,
        });
      }
    } catch (error) {
      toast.error(error.response.data.data.error);
    }
  };
const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;
  setFormValues((prev) => ({
    ...prev,
    [name]: value ? value : "",
  }));
};
  const handleImageSelected = (image) => {
    const finalImage = image[0];
    setSelectedImages(finalImage);
    setFormValues((prev) => ({
      ...prev,
      key_value: finalImage,
    }));
    setModalOpen(false);
  };

  const handleChange = (event, editor) => {
    const data = editor.getData();
    setFormValues((prevState) => ({
      ...prevState,
      key_value: data,
    }));
  };

  const handleDeleteImage = () => {
    setSelectedImages(null);
  };

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Setting</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Box p={3}>
              <Grid container spacing={2} alignItems="center" mb={2}>
                <Grid item xs={4}>
                  <Typography variant="subtitle1">
                    Settings Field Type{" "}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <FormControl
                    fullWidth
                    variant="filled"
                    error={!!errors.validation_id}
                  >
                    <InputLabel>Select type</InputLabel>
                    <Select
                      name="validation_id"
                      value={formValues.validation_id}
                      onChange={handleSelectChange}
                    >
                      {validationDropdown?.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.validation_id}</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>

              {selectedValidation && (
                <Grid container spacing={2} alignItems="center" mb={2}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle1">Select Group </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FormControl
                      fullWidth
                      variant="filled"
                      error={!!errors.group_id}
                    >
                      <InputLabel>Select Group</InputLabel>
                      <Select
                        name="group_id"
                        value={formValues.group_id}
                        onChange={handleSelectChange}
                      >
                        {groups?.map(({ id, name }) => (
                          <MenuItem key={id} value={id}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.group_id}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle1">Key Name</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      variant="filled"
                      name="key_name"
                      value={formValues.key_name}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="subtitle1">Display Name</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      variant="filled"
                      name="display_name"
                      value={formValues.display_name}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              )}

              {showDynamicSelect && (
                <Grid container spacing={2} alignItems="center" mb={2}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle1">Key value</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FormControl fullWidth>
                      <InputLabel id="dynamic-select-label">
                        Select the value
                      </InputLabel>
                      <Select
                        labelId="dynamic-select-label"
                        id="dynamic-select"
                        value={selectedKeyvalue}
                        label="Choose an option"
                        onChange={(e) => setSelectedKeyvalue(e.target.value)}
                      >
                        {dynamicOptions?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              {selectedValidation && isHeader && isFooter && (
                <>
                  {selectedValidation.name === "CK_EDITOR" ? (
                    <Grid container spacing={2} alignItems="center" mb={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle1">Key Value</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <ReusableEditor
                          value={formValues.key_value || ""}
                          onChange={(value) =>
                            setFormValues((prevState) => ({
                              ...prevState,
                              key_value: value,
                            }))
                          }
                        />
                      </Grid>
                    </Grid>
                  ) : selectedValidation.name === "IMAGE" ? (
                    <Grid container spacing={2} alignItems="center" mb={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle1">Key Value</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <div
                          style={{
                            width: "100%",
                            padding: "20px",
                            borderRadius: "10px",
                            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                            backgroundColor: "#fff",
                          }}
                        >
                          <div
                            style={{
                              textAlign: "center",
                              marginBottom: "20px",
                              border: "1px dashed #ccc",
                              padding: "20px",
                              display: "flex",
                              justifyContent: "center",
                              borderRadius: "10px",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            {selectedImages ? (
                              <>
                                <img
                                  src={`${AppUrl}${selectedImages}`}
                                  alt="Selected"
                                  style={{
                                    width: "300px",
                                    transition: "transform 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.05)";
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                  }}
                                />
                                <button
                                  style={{
                                    backgroundColor: "rgba(255, 0, 0, 0.7)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: "30px",
                                    height: "30px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
                                    transition: "background-color 0.2s",
                                  }}
                                  onClick={handleDeleteImage}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </>
                            ) : (
                              <p style={{ color: "orange" }}>
                                <FontAwesomeIcon icon={faCircleExclamation} />
                                &nbsp; &nbsp; No image selected. Please upload
                                an image.
                              </p>
                            )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              onClick={handleOpenModal}
                              variant="contained"
                              color="primary"
                            >
                              <FontAwesomeIcon icon={faFileArrowUp} /> &nbsp;
                              &nbsp; Upload Image
                            </Button>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  ) : selectedValidation.name === "PRIVACY_POLICY" ? (
                    <Grid container spacing={2} alignItems="center" mb={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle1">Select Page</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <TextField
                          select
                          variant="filled"
                          name="key_value"
                          value={formValues.key_value || ""}
                          onChange={(e) =>
                            handleInputChange({
                              target: {
                                name: "key_value",
                                value: e.target.value,
                              },
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                          fullWidth
                        >
                          {subPagesLinks?.map((page) => (
                            <MenuItem key={page.page_id} value={page.page_id}>
                              {page.page_name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  ) : selectedValidation.name === "FONT_FAMILY" ? (
                    <Grid container spacing={2} alignItems="center" mb={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle1">
                          Select Font Family
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <GoogleFontsDropdown
                          onSelectFont={handleFontSelection}
                        />
                      </Grid>
                    </Grid>
                  ) : selectedValidation.name === "COLOR_PICKER" ? (
                    <Grid container spacing={2} alignItems="center" mb={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle1">
                          Select Color
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <TextField
                            variant="filled"
                            id="background_color"
                            name="key_value"
                            value={formValues.key_value}
                            onChange={handleTextFieldChange}
                            placeholder="#000000"
                            className="common_input"
                            sx={{ width: "25%" }}
                          />
                          <input
                            type="color"
                            id="background_color"
                            name="key_value"
                            value={formValues.key_value}
                            onChange={handleTextFieldChange}
                            style={{
                              width: "50px",
                              height: "50px",
                              border: "none",
                              cursor: "pointer",
                              marginLeft: "10px",
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2} alignItems="center" mb={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle1">Key Value</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <TextField
                          variant="filled"
                          name="key_value"
                          type={
                            validationTypes[selectedValidation?.name]?.type ||
                            "text"
                          }
                          placeholder={
                            validationTypes[selectedValidation?.name]
                              ?.placeholder || "Enter value"
                          }
                          value={formValues.key_value}
                          onChange={handleInputChange}
                          fullWidth
                          error={!!errors.key_value}
                          helperText={errors.key_value}
                        />
                      </Grid>
                    </Grid>
                  )}
                </>
              )}

              <Box display="flex" justifyContent="flex-end" pb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!selectedValidation}
                >
                  <FontAwesomeIcon
                    style={{ marginRight: "10px" }}
                    icon={faCirclePlus}
                  />
                  Create Setting
                </Button>
              </Box>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      <ImageUploaderModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onImageSelect={handleImageSelected}
        allowMultiple={false}
      />
    </>
  );
};

export default SettingSingleCreate;